// ────────────────────────────────────────────────────────────────────────────
// Domain-aware brand.
//
//   connectcorporate.com  → "corporate"  → Accounting + Corporate Services
//   conncct.com (default) → "consulting" → Consulting + the Capital Readiness platform
//
// One app, one Firebase project. Accounts are brand-tagged (see signup) rather
// than living in separate auth systems (Path 1). This module resolves which
// brand the current page load belongs to.
//
// Testing: on staging / localhost (which have no corporate domain) append
// `?brand=corporate` to any URL — it's remembered for the browser session so
// you can click around the corporate experience. `?brand=consulting` resets.
// ────────────────────────────────────────────────────────────────────────────
export type Brand = 'corporate' | 'consulting';

// Hosts that should render the corporate (Accounting + Corporate Services) brand.
// (The bare *.web.app site URL is included so the corporate site is testable directly.)
const CORPORATE_HOSTS = ['connectcorporate.com', 'connectcorporate.web.app'];

function detect(): Brand {
  if (typeof window === 'undefined') return 'consulting';

  // Session override for testing on staging / localhost.
  try {
    const q = new URLSearchParams(window.location.search).get('brand');
    if (q === 'corporate' || q === 'consulting') {
      sessionStorage.setItem('brandOverride', q);
    }
    const ov = sessionStorage.getItem('brandOverride');
    if (ov === 'corporate' || ov === 'consulting') return ov;
  } catch {
    /* sessionStorage can be unavailable (privacy mode) — fall through */
  }

  const host = window.location.hostname.toLowerCase();
  if (CORPORATE_HOSTS.some(d => host === d || host === `www.${d}` || host.endsWith(`.${d}`))) {
    return 'corporate';
  }
  return 'consulting';
}

/** The brand for this page load (fixed per domain; overridable in-session for testing). */
export const BRAND: Brand = detect();
export const isCorporate = BRAND === 'corporate';
export const isConsulting = BRAND === 'consulting';

// Tag the document root so global stylesheets (e.g. publicPages.css) can theme by
// brand via CSS variables — amber on corporate, blue on consulting.
if (typeof document !== 'undefined') {
  try {
    document.documentElement.classList.remove('brand-corp', 'brand-consulting');
    document.documentElement.classList.add(BRAND === 'corporate' ? 'brand-corp' : 'brand-consulting');

    // Brand-colored favicon — gold "C" on corporate, blue "C" on consulting (no
    // static asset needed). The ink flips with the tile: navy on gold, white on
    // blue. White on gold would be invisible at 16px.
    const acc = BRAND === 'corporate' ? '#FFD54F' : '#1976D2';
    const ink = BRAND === 'corporate' ? '#17263A' : '#ffffff';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="${acc}"/><text x="32" y="46" font-family="Arial,Helvetica,sans-serif" font-size="42" font-weight="bold" fill="${ink}" text-anchor="middle">C</text></svg>`;
    const href = 'data:image/svg+xml,' + encodeURIComponent(svg);
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'icon'); document.head.appendChild(link); }
    link.setAttribute('type', 'image/svg+xml');
    link.setAttribute('href', href);
    document.title = BRAND === 'corporate' ? 'Conncct Corporate' : 'Conncct';
  } catch { /* ignore */ }
}

/** Canonical public origin for each brand. */
export const ORIGINS: Record<Brand, string> = {
  corporate: 'https://connectcorporate.com',
  consulting: 'https://conncct.com',
};

/** Human-readable domain label for messaging ("sign in at …"). */
export const brandLabel = (b: Brand): string => (b === 'corporate' ? 'connectcorporate.com' : 'conncct.com');

/**
 * Public brand NAME used in visible copy. The corporate brand is "Conncct Corporate"
 * (per the Corporate landing spec — use it, not bare "Conncct"); consulting stays "Conncct".
 */
export const BRAND_NAME: string = BRAND === 'corporate' ? 'Conncct Corporate' : 'Conncct';

/**
 * `true` now that BOTH custom domains are verified & serving. Enables hard
 * cross-domain redirects for a route that belongs to the other brand — but ONLY
 * on the real production hostnames (never staging/localhost or a ?brand override),
 * see `crossDomainRedirect`. See brand-domain-split memory.
 */
export const DOMAINS_LIVE = true;

/** Real production hostnames per brand (used to gate hard cross-domain redirects). */
const PROD_HOSTS: Record<Brand, string> = {
  corporate: 'connectcorporate.com',
  consulting: 'conncct.com',
};

/**
 * If the current page is a real production brand domain and the route being
 * viewed belongs to the OTHER brand, returns the URL to redirect to (same path)
 * on the correct domain; otherwise null. Never fires on staging/localhost or when
 * a ?brand override is active, so testing stays on one origin.
 */
export function crossDomainRedirect(routeBrand: Brand): string | null {
  if (!DOMAINS_LIVE || BRAND === routeBrand) return null;
  if (typeof window === 'undefined') return null;
  try { if (sessionStorage.getItem('brandOverride')) return null; } catch { /* ignore */ }
  const host = window.location.hostname.toLowerCase();
  const isProdHost = Object.values(PROD_HOSTS).some(d => host === d || host === `www.${d}`);
  if (!isProdHost) return null;
  return ORIGINS[routeBrand] + window.location.pathname + window.location.search;
}

/** The logged-in dashboard's product view for this brand. */
export const BRAND_SERVICE_VIEW: 'accounting' | 'consulting' = BRAND === 'corporate' ? 'accounting' : 'consulting';
